import { createRouter, createWebHistory } from "vue-router";
import { keycloakAuthService } from "@/infrastructure/adapters/KeycloakAuthService";
import { logger } from "@/infrastructure/services/LoggerService";
import { clearOrganizationCache } from "@/shared/utils/authCleanup";
import { resolveCapabilityRedirect } from "@/shared/utils/routeCapabilityGuard";
import { resolveLandingRedirect } from "@/shared/utils/landingPage";
import { handleRouteScroll, rememberScrollOffset } from "@/shared/utils/routeScrollBehavior";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import Logout from "@/presentation/views/Logout.vue";
import NotFound from "@/presentation/views/NotFound.vue";
import Error from "@/presentation/views/Error.vue";
/**
 * Shell-specific routes (Error, NotFound, SessionEnded, etc.)
 * Module routes will be dynamically added in main.ts via moduleRegistry
 *
 * Note: ProfileSettings route is now provided by settings module
 */
export const shellRoutes = [
    // "/" is owned by the home module (HomeView) — no shell redirect.
    {
        path: "/session-ended",
        name: "SessionEnded",
        component: Logout,
        meta: { requiresAuth: false, isLoginPage: true, titleKey: "logout.title" },
    },
    {
        // `:code?` lets callers route to /error/403, /error/502, /error/503,
        // /error/504, /error/500 (or just /error → 500 fallback). The view
        // whitelists the value, so a bogus /error/999 still renders 500.
        path: "/error/:code?",
        name: "Error",
        component: Error,
        meta: { requiresAuth: false, isPublicPage: true },
    },
    {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: NotFound,
        meta: { requiresAuth: false, isPublicPage: true, titleKey: "errorPage.notFound.title" },
    },
];
// Export routes for backward compatibility
export const routes = shellRoutes;
// Create router with shell routes only initially
// Module routes will be added before app.mount() in main.ts
const router = createRouter({
    history: createWebHistory(),
    routes: shellRoutes,
    scrollBehavior: handleRouteScroll,
});
/**
 * Remembers where to send the user after a forced login.
 *
 * The root is deliberately NOT stored: it is the default post-login destination
 * anyway, and the `afterEach` cleanup below clears the key only when the user
 * actually arrives at the stored path. Since the OP-6808 landing redirect rewrites
 * every navigation to "/" into the landing page, a stored "/" could never be
 * matched again — it would survive the whole session and hijack the next
 * /session-ended navigation, bouncing the user to the landing page instead of
 * showing the session-expired screen. Keep this on OP-6808 removal: it is correct
 * either way, and only the landing redirect made it observable.
 */
function rememberRedirectPath(cleanPath) {
    if (cleanPath === "/") {
        return;
    }
    sessionStorage.setItem("auth-redirect-path", cleanPath);
}
function waitForAuthInitialization() {
    return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 100;
        const checkInitialization = () => {
            if (keycloakAuthService.isInitialized()) {
                resolve();
            }
            else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkInitialization, 100);
            }
            else {
                logger.debug("Authentication service initialization timeout");
                resolve();
            }
        };
        checkInitialization();
    });
}
router.beforeEach(async (to, from, next) => {
    logger.debug(`Navigation: ${from.path} -> ${to.path}`);
    // Last point where the outgoing page is still on screen, so its offset can be recorded for a
    // later back/forward. Kept above every early return below: the guards must not skip it.
    rememberScrollOffset(from.fullPath);
    // Allow direct access to session-ended page without auth checks
    if (to.path === "/session-ended") {
        logger.debug("Navigating to session-ended page, skipping auth checks");
        next();
        return;
    }
    const stayOnErrorPage = sessionStorage.getItem("stay-on-error-page");
    if (to.name === "Error" && stayOnErrorPage) {
        next();
        return;
    }
    try {
        const hash = window.location.hash;
        if (hash.includes("error=login_required") &&
            !keycloakAuthService.isAuthenticated() &&
            to.name !== "Error") {
            if (to.path !== "/session-ended" && !to.meta?.isPublicPage) {
                await waitForAuthInitialization();
                const cleanPath = to.path +
                    (to.query && Object.keys(to.query).length > 0
                        ? "?" + new URLSearchParams(to.query).toString()
                        : "");
                rememberRedirectPath(cleanPath);
                const currentUrl = window.location.origin + cleanPath;
                // OP-5119: drop the cached org so the forced login goes out with the
                // plain `organization` scope. The silent SSO that just failed used
                // `organization:<cachedOrg>` — if we redirected with that same scope,
                // a user signing in with credentials that don't belong to the cached
                // org would loop or be assigned the wrong org.
                clearOrganizationCache();
                try {
                    await keycloakAuthService.login({ redirectUri: currentUrl });
                    return;
                }
                catch (error) {
                    logger.debug("Failed to trigger login:", error);
                    next({ path: "/session-ended" });
                    return;
                }
            }
        }
        if (keycloakAuthService.isAuthenticated() &&
            hash &&
            (hash.includes("error=") || hash.includes("code=") || hash.includes("state="))) {
            const cleanPath = to.path +
                (to.query && Object.keys(to.query).length > 0
                    ? "?" + new URLSearchParams(to.query).toString()
                    : "");
            window.history.replaceState({}, document.title, cleanPath);
        }
        if (to.meta?.isPublicPage) {
            next();
            return;
        }
        if (to.meta?.isLoginPage) {
            await waitForAuthInitialization();
            if (keycloakAuthService.isAuthenticated() && !hash.includes("error=")) {
                next({ path: "/" });
                return;
            }
            sessionStorage.removeItem("force-logout");
            next();
            return;
        }
        const forceLogout = sessionStorage.getItem("force-logout");
        if (forceLogout) {
            const logoutTime = parseInt(forceLogout);
            const currentTime = Date.now();
            const timeDiff = currentTime - logoutTime;
            if (timeDiff < 5 * 60 * 1000) {
                if (!hash.includes("error=login_required")) {
                    await waitForAuthInitialization();
                    if (!keycloakAuthService.isAuthenticated()) {
                        if (from.path === "/session-ended" ||
                            to.path === "/session-ended" ||
                            to.name === "SessionEnded") {
                            sessionStorage.removeItem("force-logout");
                            next();
                            return;
                        }
                        else if (to.matched.some((record) => record.meta.requiresAuth)) {
                            next(false);
                            return;
                        }
                    }
                }
            }
            else {
                sessionStorage.removeItem("force-logout");
            }
        }
        if (to.matched.some((record) => record.meta.requiresAuth)) {
            await waitForAuthInitialization();
            if (!keycloakAuthService.isAuthenticated()) {
                const cleanPath = to.path +
                    (to.query && Object.keys(to.query).length > 0
                        ? "?" + new URLSearchParams(to.query).toString()
                        : "");
                rememberRedirectPath(cleanPath);
                const currentUrl = window.location.origin + cleanPath;
                // OP-5119: see comment above the login_required branch.
                clearOrganizationCache();
                try {
                    await keycloakAuthService.login({ redirectUri: currentUrl });
                    return;
                }
                catch (error) {
                    logger.debug("Failed to trigger login:", error);
                    next(false);
                    return;
                }
            }
        }
        // Capability gating (defense-in-depth; backend still enforces 403).
        // Only routes that declare meta.requiresCapability are affected. Ensure the
        // profile (which carries the capability set) is loaded before deciding.
        if (to.matched.some((record) => record.meta?.requiresCapability)) {
            try {
                const profile = await useProfileStore().fetchProfile();
                const redirect = resolveCapabilityRedirect(to, profile.permissions ?? []);
                if (redirect) {
                    next(redirect);
                    return;
                }
            }
            catch (capError) {
                logger.debug("Capability guard failed to resolve profile:", capError);
                next({ path: "/error/403" });
                return;
            }
        }
        // TEMPORARY (OP-6808) — per-environment landing page, superseded by OP-6322.
        // Placed last so auth and capability checks have already run: the redirect
        // must not pre-empt a login or a 403.
        const landingRedirect = await resolveLandingRedirect(router, to.path, async () => {
            const profile = await useProfileStore().fetchProfile();
            return profile.permissions ?? [];
        });
        if (landingRedirect) {
            next({ path: landingRedirect, replace: true });
            return;
        }
        next();
    }
    catch (error) {
        logger.debug("Navigation guard error:", error);
        if (!to.matched.some((record) => record.meta.requiresAuth)) {
            next();
        }
        else {
            try {
                const cleanPath = to.path +
                    (to.query && Object.keys(to.query).length > 0
                        ? "?" + new URLSearchParams(to.query).toString()
                        : "");
                rememberRedirectPath(cleanPath);
                const currentUrl = window.location.origin + cleanPath;
                // OP-5119: clear cached org before forced login (see other branches).
                clearOrganizationCache();
                await keycloakAuthService.login({ redirectUri: currentUrl });
                return;
            }
            catch (loginError) {
                logger.debug("Failed to trigger login on error:", loginError);
                next(false);
            }
        }
    }
});
router.afterEach((to) => {
    const hash = window.location.hash;
    if (hash &&
        (hash.includes("error=") ||
            hash.includes("code=") ||
            hash.includes("state=") ||
            hash.includes("session_state="))) {
        const cleanPath = to.path +
            (to.query && Object.keys(to.query).length > 0
                ? "?" + new URLSearchParams(to.query).toString()
                : "");
        window.history.replaceState({}, document.title, cleanPath);
    }
    if (to.name === "Error") {
        return;
    }
    const redirectPath = sessionStorage.getItem("auth-redirect-path");
    if (keycloakAuthService.isAuthenticated() && redirectPath) {
        if (to.path === "/session-ended") {
            sessionStorage.removeItem("auth-redirect-path");
            setTimeout(() => {
                router.push(redirectPath);
            }, 100);
            return;
        }
        if (to.fullPath === redirectPath || to.path === redirectPath) {
            sessionStorage.removeItem("auth-redirect-path");
        }
    }
    if (to.path === "/session-ended" && keycloakAuthService.isAuthenticated() && !redirectPath) {
        setTimeout(() => {
            router.push("/");
        }, 100);
        return;
    }
});
export default router;
